// apps/client/src/hooks/useEdmJourneys.ts
//
// Plan JOURNEYS J8: typed wrappers over the `edmJourneyAction` callable
// (`{ action, companyId, ...fields }`), the `edm_journeys` flag helper and the
// refusal copy for the journey codes. Journey state is read ONLY through the
// callable (edm_journeys is client read/write false in firestore.rules).

import { useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { edmErrorMessage, edmErrorReason, isEdmEnabled } from '../utils/edm';
import { EDM_CALLABLE_OPTIONS, type EdmAudience, type EdmAudienceCounters } from './useEdmCampaigns';

/** `edm_journeys`: strictly `=== true` AND `edm_broadcast === true` (mirrors the server helper). */
export const isEdmJourneysEnabled = (f?: Record<string, unknown> | null): boolean =>
  isEdmEnabled(f) && f?.edm_journeys === true;

export type EdmJourneyStatus = 'draft' | 'active' | 'paused' | 'archived';

export type EdmJourneyTrigger =
  | { type: 'settlement_anniversary' }
  | { type: 'fixed_rate_expiry'; leadDays: number }
  | { type: 'tag_event'; tagNorm: string; offsetDays: number };

export interface EdmJourneyStep {
  stepId: string;
  stepTemplateId: string;
  offsetDays: number;
}

export interface EdmJourneyStepDetail extends EdmJourneyStep {
  subject: string;
  hasAbTest: boolean;
  /** A delivered test within 7 days, sent after the step's last content change. */
  tested: boolean;
  testDeliveredAt: string | null;
  lastTestSentAt: string | null;
}

export interface EdmJourneyProjection {
  id: string;
  name: string;
  status: EdmJourneyStatus;
  revision: number;
  trigger: EdmJourneyTrigger;
  audience: EdmAudience;
  steps: EdmJourneyStep[];
  sendAsBrokerUid: string | null;
  topicId: string | null;
  createdBy: { uid: string };
  activatedAt: string | null;
  pausedReason: string | null;
  fromDomain: string | null;
  updatedAt: string | null;
}

export type EdmJourneyDetail = EdmJourneyProjection & { stepDetails: EdmJourneyStepDetail[] };

export interface EdmJourneyChildRow {
  id: string;
  stepId: string | null;
  day: string | null;
  revision: number | null;
  status: string;
  sentCount: number;
  totalCount: number;
  scheduledAt: string | null;
  completedAt: string | null;
  reason: string | null;
}

export interface EdmJourneyStats {
  totals: { children: number; sent: number; recipients: number; uniqueOpens: number; uniqueClicks: number };
  enrolments: { active: number; completed: number; exited: number };
  children: EdmJourneyChildRow[];
}

export interface EdmJourneyApi {
  list: () => Promise<{ journeys: EdmJourneyProjection[] }>;
  get: (journeyId: string) => Promise<EdmJourneyDetail>;
  create: (fields: { name: string; trigger: EdmJourneyTrigger; audience: EdmAudience }) => Promise<{ journeyId: string }>;
  update: (fields: {
    journeyId: string;
    name?: string;
    trigger?: EdmJourneyTrigger;
    audience?: EdmAudience;
    sendAsBrokerUid?: string;
    topicId?: string | null;
  }) => Promise<EdmJourneyDetail>;
  addStep: (journeyId: string, offsetDays: number) => Promise<{ stepId: string; stepTemplateId: string }>;
  removeStep: (journeyId: string, stepId: string) => Promise<EdmJourneyDetail>;
  reorder: (journeyId: string, steps: Array<{ stepId: string; offsetDays: number }>) => Promise<EdmJourneyDetail>;
  activate: (journeyId: string) => Promise<{ journey: EdmJourneyProjection; counters: EdmAudienceCounters }>;
  resume: (journeyId: string) => Promise<{ journey: EdmJourneyProjection; counters: EdmAudienceCounters }>;
  pause: (journeyId: string) => Promise<EdmJourneyProjection>;
  archive: (journeyId: string) => Promise<EdmJourneyProjection>;
  stats: (journeyId: string) => Promise<EdmJourneyStats>;
}

async function callEdmJourneyAction<Res>(action: string, payload: Record<string, unknown>): Promise<Res> {
  const fn = httpsCallable<Record<string, unknown>, Res>(functions, 'edmJourneyAction', EDM_CALLABLE_OPTIONS);
  const res = await fn({ action, ...payload });
  return res.data;
}

export function useEdmJourneys(companyId: string | undefined): EdmJourneyApi {
  const call = useCallback(
    <Res,>(action: string, payload: Record<string, unknown> = {}) => {
      if (!companyId) return Promise.reject(new Error('companyId required'));
      return callEdmJourneyAction<Res>(action, { companyId, ...payload });
    },
    [companyId],
  );
  return useMemo<EdmJourneyApi>(
    () => ({
      list: () => call('list'),
      get: (journeyId) => call('get', { journeyId }),
      create: (fields) => call('create', { ...fields }),
      update: (fields) => call('update', { ...fields }),
      addStep: (journeyId, offsetDays) => call('addStep', { journeyId, offsetDays }),
      removeStep: (journeyId, stepId) => call('removeStep', { journeyId, stepId }),
      reorder: (journeyId, steps) => call('reorder', { journeyId, steps }),
      activate: (journeyId) => call('activate', { journeyId }),
      resume: (journeyId) => call('resume', { journeyId }),
      pause: (journeyId) => call('pause', { journeyId }),
      archive: (journeyId) => call('archive', { journeyId }),
      stats: (journeyId) => call('stats', { journeyId }),
    }),
    [call],
  );
}

export const JOURNEY_STATUS_LABEL: Record<EdmJourneyStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  archived: 'Archived',
};

export function triggerLabel(t: EdmJourneyTrigger): string {
  if (t.type === 'settlement_anniversary') return 'Settlement anniversary';
  if (t.type === 'fixed_rate_expiry') return `Fixed rate ends in ${t.leadDays} days`;
  const when = t.offsetDays === 0 ? 'on the day' : t.offsetDays < 0 ? `${-t.offsetDays} days before` : `${t.offsetDays} days after`;
  return `Tag "${t.tagNorm}" event date, ${when}`;
}

/** Why a journey was paused, in plain words. */
export function journeyPausedReasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  if (reason === 'paused_by_user') return 'Paused by a team member.';
  if (reason === 'activator_scope_changed') return 'Paused: the person who started this journey can no longer send to its audience.';
  if (reason === 'reputation' || reason === 'bounce_rate') return 'Paused: too many emails bounced or were marked as spam. A Super Admin must resume it.';
  if (reason.startsWith('delivery_feedback_')) return 'Paused: delivery confirmations stopped arriving. A Super Admin must resume it.';
  return `Paused (${reason}).`;
}

/** Refusal copy for the journey codes; anything else uses the eDM copy. */
export function journeyErrorMessage(err: unknown): string {
  const reason = edmErrorReason(err);
  const details = ((err as { details?: unknown } | null)?.details ?? {}) as Record<string, unknown>;
  switch (reason) {
    case 'edm_disabled':
      return 'Journeys are not switched on for your company.';
    case 'journeys_company_wide_only':
      return 'Journeys are available to company-wide roles only.';
    case 'journey_not_found':
      return 'That journey no longer exists.';
    case 'journey_not_accessible':
      return 'You cannot manage this journey.';
    case 'journey_structure_locked':
      return 'People are part-way through this journey. Archive it and create a new one to change its steps, trigger or audience.';
    case 'journey_steps_invalid':
      return 'A journey has 1 to 5 steps: the first on day 0 and each later step on a later day (up to 365 days).';
    case 'journey_audience_invalid':
      return 'Settlement anniversary and fixed-rate journeys email clients only. Choose at least one audience.';
    case 'journey_limit':
      return 'You already have 5 active journeys. Pause or archive one first.';
    case 'journey_step_untested':
      return 'Send a test of every step and wait for it to be delivered before activating. A test sent before your last edit does not count.';
    case 'journey_ab_unsupported':
      return 'Journey emails cannot use an A/B subject test.';
    case 'journey_no_eligible_recipients':
      return 'Nobody in this audience can be emailed yet (no consented, current recipients).';
    case 'journey_activator_not_allowed':
      return 'Your account cannot start this journey.';
    case 'journey_active':
      return 'Pause the journey before editing its emails.';
    case 'journey_step_managed':
    case 'journey_child_managed':
      return 'This email is sent by its journey. Manage it from the journey.';
    case 'journey_changed':
      return 'This journey changed while you were working. Refresh and try again.';
    case 'journey_step_not_found':
      return typeof details.stepId === 'string' ? 'A step of this journey is missing. Remove it and add it again.' : 'That step no longer exists.';
    case 'resume_requires_super_admin':
      return 'Only a Super Admin can resume this journey.';
    case 'topic_required':
      return 'Choose which topic this journey is about.';
    default:
      return edmErrorMessage(err);
  }
}
